/**
 * Gamepad adapter. Poll-based via `navigator.getGamepads()`, which is the only correct way to
 * read gamepads — there are no per-button events.
 *
 * One of the two files the determinism lint exempts for DOM access (ARCHITECTURE.md §4.1).
 *
 * Analogue sticks get HYSTERESIS: a direction engages at the deadzone and only disengages at 70 %
 * of it. Without that, a stick resting near the threshold chatters on and off every poll, which
 * reads to the command recogniser as a stream of dash inputs.
 */

import { STICK_DEADZONE } from '../../core/constants.js';
import { ACTIONS } from '../bindings.js';

/** Analogue value at which a held direction releases, as a fraction of the engage threshold. */
const RELEASE_FACTOR = 0.7;
/** Trigger axes/buttons read as pressed above this. */
const TRIGGER_THRESHOLD = 0.45;

export class GamepadDevice {
  constructor(bindings) {
    this.bindings = bindings;
    this.available = typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function';
    /** Which gamepad index drives each player slot; -1 = none. */
    this.assignment = [-1, -1];
    /** Latched analogue direction state per player, for hysteresis. */
    this._axisHeld = [
      { up: false, down: false, left: false, right: false },
      { up: false, down: false, left: false, right: false },
    ];
    this._pads = [];
    this.stats = { connected: 0, polls: 0 };
  }

  attach(bag) {
    if (!this.available) return this;
    const onConnect = (e) => this._assign(e.gamepad);
    const onDisconnect = (e) => this._unassign(e.gamepad);
    if (bag && typeof window !== 'undefined') {
      bag.listener(window, 'gamepadconnected', onConnect);
      bag.listener(window, 'gamepaddisconnected', onDisconnect);
    }
    // Pads connected before the listeners existed are picked up on the first poll.
    this.poll();
    return this;
  }

  _assign(pad) {
    if (!pad) return;
    if (this.assignment.includes(pad.index)) return;
    const slot = this.assignment.indexOf(-1);
    if (slot >= 0) {
      this.assignment[slot] = pad.index;
      this.stats.connected++;
    }
  }

  _unassign(pad) {
    if (!pad) return;
    const slot = this.assignment.indexOf(pad.index);
    if (slot >= 0) {
      this.assignment[slot] = -1;
      const held = this._axisHeld[slot];
      held.up = false;
      held.down = false;
      held.left = false;
      held.right = false;
    }
  }

  /** Refreshes the pad snapshot. Must be called once per tick before `readInto`. */
  poll() {
    if (!this.available) return;
    this.stats.polls++;
    this._pads = navigator.getGamepads ? navigator.getGamepads() : [];
    // Auto-assign any pad that appeared without firing an event (common on page reload).
    for (let i = 0; i < this._pads.length; i++) {
      const pad = this._pads[i];
      if (pad && pad.connected) this._assign(pad);
    }
    // Drop assignments whose pad vanished.
    for (let slot = 0; slot < 2; slot++) {
      const idx = this.assignment[slot];
      if (idx < 0) continue;
      const pad = this._pads[idx];
      if (!pad || !pad.connected) this.assignment[slot] = -1;
    }
  }

  hasPad(player) {
    return this.assignment[player] >= 0;
  }

  /**
   * Writes this player's raw action state into `out`. Returns false when no pad is assigned, so
   * the input system can fall back to the keyboard for that slot.
   */
  readInto(out, player) {
    const idx = this.assignment[player];
    if (idx < 0) return false;
    const pad = this._pads[idx];
    if (!pad || !pad.connected) return false;

    const map = this.bindings.gamepad;
    const buttons = pad.buttons;
    const axes = pad.axes;

    for (let i = 0; i < ACTIONS.length; i++) out[ACTIONS[i]] = false;

    // --- face buttons, with shoulder-or-trigger alternates for sp/guard ---
    out.lp = pressed(buttons, map.lp);
    out.hp = pressed(buttons, map.hp);
    out.lk = pressed(buttons, map.lk);
    out.hk = pressed(buttons, map.hk);
    out.sp = pressed(buttons, map.sp) || pressed(buttons, map.spAlt);
    out.guard = pressed(buttons, map.guard) || pressed(buttons, map.guardAlt);

    // --- d-pad is digital and authoritative when held ---
    let up = pressed(buttons, map.dpadUp);
    let down = pressed(buttons, map.dpadDown);
    let left = pressed(buttons, map.dpadLeft);
    let right = pressed(buttons, map.dpadRight);

    // --- analogue stick with hysteresis ---
    const held = this._axisHeld[player];
    const ax = axes && axes.length > map.axisX ? axes[map.axisX] : 0;
    const ay = axes && axes.length > map.axisY ? axes[map.axisY] : 0;
    held.right = latch(held.right, ax, 1);
    held.left = latch(held.left, ax, -1);
    held.down = latch(held.down, ay, 1); // gamepad Y is inverted: +1 is down
    held.up = latch(held.up, ay, -1);

    out.up = up || held.up;
    out.down = down || held.down;
    out.left = left || held.left;
    out.right = right || held.right;

    // Opposing directions cancel, which is what a d-pad physically does and what stops a
    // simultaneous left+right from resolving to an arbitrary winner.
    if (out.left && out.right) {
      out.left = false;
      out.right = false;
    }
    if (out.up && out.down) {
      out.up = false;
      out.down = false;
    }
    return true;
  }

  /** Assigns a specific pad to a slot; used by the settings screen. */
  setAssignment(player, padIndex) {
    this.assignment[player] = padIndex;
  }

  connectedPads() {
    const out = [];
    for (let i = 0; i < this._pads.length; i++) {
      const pad = this._pads[i];
      if (pad && pad.connected) out.push({ index: i, id: pad.id, mapping: pad.mapping });
    }
    return out;
  }

  detach() {
    this._pads = [];
    this.assignment[0] = -1;
    this.assignment[1] = -1;
  }
}

function pressed(buttons, index) {
  if (index === undefined || !buttons || index >= buttons.length) return false;
  const b = buttons[index];
  if (!b) return false;
  // Triggers report an analogue `value`; face buttons report `pressed`.
  return b.pressed === true || (typeof b.value === 'number' && b.value >= TRIGGER_THRESHOLD);
}

/**
 * Hysteresis latch: engage at the full deadzone, release only below 70 % of it.
 * @param {boolean} was current latched state
 * @param {number} value raw axis value
 * @param {number} sign +1 or -1, the direction this latch watches
 */
function latch(was, value, sign) {
  const v = value * sign;
  return was ? v > STICK_DEADZONE * RELEASE_FACTOR : v > STICK_DEADZONE;
}
